import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button, ButtonProps } from '@osmosis/tailwind-ui/components/Button';
import React from 'react';
import Image from 'next/legacy/image';
import { FormattedMessage } from 'react-intl';
import facebookLogo from './../../assets/facebook-logo.svg';
import googleLogo from './../../assets/google-logo.svg';
import appleLogo from './../../assets/apple-logo.svg';
import AuthDivider from './AuthDivider';
import { useSearchParams } from 'next/navigation';

const SocialButton = (props: ButtonProps) => (
  <Button
    rounded
    as="a"
    data-testid={props['data-testid']}
    variant="outline"
    startIcon={
      <Image
        src={facebookLogo}
        width={25}
        height={25}
        alt="Facebook"
        className="mr-2 align-middle"
      />
    }
    {...props}
    style={{
      borderColor: 'primaryText',
      ...props.style,
    }}
    className={`flex text-center px-[16px] pt-[6px] pb-[4px] flex items-center justify-center cursor-pointer font-bold bg-white text-blue-800 border-2 border-blue-800 rounded-full w-full`}
  />
);

type LoginFormFooterProps = {
  isSignUp?: boolean;
};

/**
 * LoginFormFooter.
 */
const LoginFormFooter = ({ isSignUp }: LoginFormFooterProps) => {
  const searchParams = useSearchParams();
  const redirectPath = searchParams?.get('afterAuthRedirect');

  const getAuthUrl = (connection: string) => {
    const baseUrl = `/neologin?connection=${connection}`;
    if (redirectPath) {
      return `${baseUrl}&afterAuthRedirect=${encodeURIComponent(redirectPath)}`;
    }
    return baseUrl;
  };

  const facebookButtonText = isSignUp
    ? 'Sign Up with Facebook'
    : 'Sign In with Facebook';
  const appleButtonText = isSignUp
    ? 'Sign Up with Apple'
    : 'Sign In with Apple';
  const googleButtonText = isSignUp
    ? 'Sign Up with Google'
    : 'Sign In with Google';

  return (
    <Box>
      <AuthDivider />
      <Flex className={`mt-4 gap-4 flex-col`}>
        <SocialButton
          startIcon={
            <Image
              src={googleLogo}
              width={25}
              height={25}
              alt="Google"
              className="mr-2 align-middle"
            />
          }
          data-testid="google-login-btn"
          href={getAuthUrl('google')}
        >
          <FormattedMessage
            id="login.google"
            defaultMessage="{buttonText}"
            values={{ buttonText: googleButtonText }}
          />
        </SocialButton>

        <SocialButton
          startIcon={
            <Image
              src={appleLogo}
              width={25}
              height={25}
              alt="Apple"
              className="mr-2 align-middle"
            />
          }
          data-testid="apple-login-btn"
          href={getAuthUrl('apple')}
        >
          <FormattedMessage
            id="login.apple"
            defaultMessage="{buttonText}"
            values={{ buttonText: appleButtonText }}
          />
        </SocialButton>

        <SocialButton
          startIcon={
            <Image
              src={facebookLogo}
              width={25}
              height={25}
              alt="Facebook"
              className="mr-2 align-middle"
            />
          }
          data-testid="facebook-login-btn"
          href={getAuthUrl('facebook')}
        >
          <FormattedMessage
            id="login.facebook"
            defaultMessage="{buttonText}"
            values={{ buttonText: facebookButtonText }}
          />
        </SocialButton>
      </Flex>
    </Box>
  );
};

export default LoginFormFooter;
